import { useEffect, useRef, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { SpeakButton } from "@/components/SpeakButton";
import { useAuth } from "@/context/AuthContext";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LANGUAGES } from "@/lib/languages";
import { Mic, Square, Loader2, Send, Languages, MessageCircleQuestion } from "lucide-react";
import { toast } from "sonner";

export default function Ask() {
  const { user, updateLanguage } = useAuth();
  const [language, setLanguage] = useState(user?.language || "English");
  const [fields, setFields] = useState([]);
  const [fieldId, setFieldId] = useState("none");
  const [typed, setTyped] = useState("");
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [turns, setTurns] = useState([]);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => { apiClient.get("/fields").then((r) => { setFields(r.data); if (r.data[0]) setFieldId(r.data[0].id); }).catch(() => {}); }, []);
  const changeLanguage = (l) => { setLanguage(l); updateLanguage(l); };

  const ask = async (payload) => {
    setBusy(true);
    try {
      const res = await apiClient.post("/ask", { ...payload, language, field_id: fieldId === "none" ? null : fieldId });
      setTurns((t) => [{ id: Date.now(), ...res.data }, ...t]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const startRec = async () => {
    if (!navigator.mediaDevices?.getUserMedia) return toast.error("Microphone not available — type your question instead");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 1000) return toast.error("Recording too short — hold the button while you speak");
        const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(blob); });
        ask({ audio_base64: b64, mime_type: blob.type });
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch { toast.error("Could not access the microphone"); }
  };

  const stopRec = () => { recRef.current?.stop(); recRef.current = null; setRecording(false); };
  const sendTyped = () => { if (!typed.trim()) return; ask({ question: typed.trim() }); setTyped(""); };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-emerald-950 tracking-tight flex items-center gap-3"><MessageCircleQuestion className="h-8 w-8 text-[#D97706]" /> Ask AgroTerra</h1>
        <p className="text-stone-600 mt-1 mb-6">Hold the mic, ask in your own language, and hear the answer back.</p>

        <div className="flex flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-xl px-3 py-2">
            <Languages className="h-4 w-4 text-[#2D6A4F]" />
            <Select value={language} onValueChange={changeLanguage}>
              <SelectTrigger data-testid="ask-language-select" className="w-[150px] h-8 border-0 bg-transparent"><SelectValue /></SelectTrigger>
              <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Select value={fieldId} onValueChange={setFieldId}>
            <SelectTrigger data-testid="ask-field-select" className="w-[240px] bg-white"><SelectValue placeholder="About which field?" /></SelectTrigger>
            <SelectContent>
              {fields.map((f) => <SelectItem key={f.id} value={f.id}>About {f.name}</SelectItem>)}
              <SelectItem value="none">General question</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white rounded-3xl border border-stone-200 p-8 flex flex-col items-center gap-4 mb-6" data-testid="ask-panel">
          <button data-testid="ask-mic-btn" onMouseDown={startRec} onMouseUp={stopRec} onTouchStart={(e) => { e.preventDefault(); startRec(); }} onTouchEnd={(e) => { e.preventDefault(); stopRec(); }}
            disabled={busy} className={`h-28 w-28 rounded-full flex items-center justify-center text-white shadow-xl transition-all select-none ${recording ? "bg-red-600 scale-110 animate-pulse" : busy ? "bg-stone-400" : "bg-[#2D6A4F] hover:bg-[#143D29] hover:scale-105"}`}>
            {busy ? <Loader2 className="h-10 w-10 animate-spin" /> : recording ? <Square className="h-10 w-10" /> : <Mic className="h-10 w-10" />}
          </button>
          <div className="text-sm text-stone-600 font-medium" data-testid="ask-status">{busy ? "Listening and thinking…" : recording ? "Release to send" : "Hold to talk"}</div>
          <div className="flex w-full gap-2 mt-2">
            <Input data-testid="ask-text-input" value={typed} onChange={(e) => setTyped(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendTyped()} placeholder="…or type your question" className="bg-white" />
            <Button data-testid="ask-send-btn" onClick={sendTyped} disabled={busy || !typed.trim()} className="bg-[#D97706] hover:bg-[#78350F] text-white"><Send className="h-4 w-4" /></Button>
          </div>
        </div>

        <div className="space-y-4" data-testid="ask-turns">
          {turns.map((t, i) => (
            <div key={t.id} data-testid={`ask-turn-${i}`} className="bg-white rounded-2xl border border-stone-200 p-5">
              <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-1">You asked</div>
              <p className="text-emerald-950 font-semibold mb-3" data-testid="ask-question">{t.question}</p>
              <div className="text-xs uppercase tracking-wider text-[#2D6A4F] font-semibold mb-1">AgroTerra</div>
              <p className="text-stone-700 leading-relaxed mb-3" data-testid="ask-answer">{t.answer}</p>
              <SpeakButton text={t.answer} language={language} prefetch={i === 0} size="sm" label="Hear answer" />
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
